<script setup lang="ts">
import { useFormat } from '@/composables/format';
import useCounter from '@/composables/useCounter';
import { dealerOrderEditTimeLimit, dealerOrderEditWarningTimeLimit } from '@/constants/order-update.constants';
import { OrderEditTimeoutStatus } from '@/models/order-edit-timeout-status.enum';
import { computed, onMounted, watch } from 'vue';

interface Props {
  orderCreatedAt: Date;
}

const { orderCreatedAt } = defineProps<Props>();

const { minutes, seconds } = useCounter(orderCreatedAt, dealerOrderEditTimeLimit);

const { formatTimeForTwoDigits } = useFormat();

const emitTimeoutReached = defineEmits(['timeout-reached']);

const orderEditTimeoutStatus = computed(() => {
  if (minutes.value === 0 && seconds.value === 0) {
    return OrderEditTimeoutStatus.Timeout;
  }
  if (minutes.value < dealerOrderEditWarningTimeLimit) {
    return OrderEditTimeoutStatus.Warning;
  }

  return OrderEditTimeoutStatus.Info;
});

const orderEditClass = computed(() => {
  switch (orderEditTimeoutStatus.value) {
    case OrderEditTimeoutStatus.Timeout:
      return 'timeout';
    case OrderEditTimeoutStatus.Warning:
      return 'warning';
    default:
      return 'info';
  }
});

onMounted(() => {
  if (minutes.value === 0 && seconds.value === 0) {
    emitTimeoutReached('timeout-reached');
  }
});

watch(orderEditTimeoutStatus, (newStatus) => {
  if (newStatus === OrderEditTimeoutStatus.Timeout) {
    emitTimeoutReached('timeout-reached');
  }
});
</script>

<template>
  <div class="info-container" :class="orderEditClass">
    <p v-if="orderEditTimeoutStatus === OrderEditTimeoutStatus.Timeout">
      {{ $t('create_edit_order.order_edit_timeout_info.timeout') }}
    </p>
    <p v-else>
      {{
        $t(
          `create_edit_order.order_edit_timeout_info.${orderEditTimeoutStatus === OrderEditTimeoutStatus.Info ? 'info' : 'warning'}`,
        ) +
        ' ' +
        formatTimeForTwoDigits(minutes) +
        ':' +
        formatTimeForTwoDigits(seconds)
      }}
    </p>
  </div>
</template>

<style lang="scss" scoped>
.info-container {
  display: flex;
  justify-content: center;
  align-items: center;
  width: 100%;
  max-width: 1440px;
  margin: 1rem auto;
  border-radius: 0.5rem;
}

.info {
  background-color: var(--shade-2);
  color: var(--primary-text-color);
}

.warning,
.timeout {
  background-color: var(--secondary-color);
  color: var(--secondary-text-color);
}
</style>
